import { MultipleChoiceQuizQuestion } from '@shared/types';
import { Checkbox } from 'components/_ui/Checkbox';
import { Pane } from 'evergreen-ui';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { Feedback } from '../../Feedback';
import { QuestionLabel } from '../../QuestionLabel';
import { BaseQuestionProps } from '../../types';

type MultipleChoiceQuestionProps = BaseQuestionProps & {
    question: MultipleChoiceQuizQuestion;
};
export const MultipleChoiceQuestion: FunctionComponent<MultipleChoiceQuestionProps> = ({
    question,
    studentAnswers,
    setStudentAnswers,
    quizDisabled,
    markedResult,
    feedbackCorrect,
    feedbackWrong,
    onQuestionBlur,
}) => {
    const answer = studentAnswers[question._id];
    const selectedOptions = useMemo(
        () => (Array.isArray(answer) ? (answer as string[]) : []),
        [answer]
    );

    const handleCheckboxChanged = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setStudentAnswers((prevStudentAnswers) => {
                const currentAnswers = (prevStudentAnswers[question._id] || []) as string[];
                if (e.target.checked) {
                    return {
                        ...prevStudentAnswers,
                        [question._id]: question.multiple_answers_allowed
                            ? [...currentAnswers, e.target.value]
                            : [e.target.value],
                    };
                }

                const answersWithTargetRemoved = currentAnswers.filter(
                    (item) => item !== e.target.value
                );

                if (answersWithTargetRemoved.length === 0) {
                    const { [question._id]: _, ...rest } = prevStudentAnswers;
                    return rest;
                }
                return {
                    ...prevStudentAnswers,
                    [question._id]: answersWithTargetRemoved,
                };
            });
        },
        [question, setStudentAnswers]
    );

    return (
        <Pane key={question._id} marginY={15} onBlur={onQuestionBlur}>
            <QuestionLabel
                label={question.question}
                markedResult={markedResult}
                htmlFor={question._id}
                description={
                    question.multiple_answers_allowed
                        ? `Select ${question.multiple_answers_match} that apply`
                        : ''
                }
            />
            {question.options.map((option: string) => {
                return (
                    <Checkbox
                        name={`${question.question} - ${option}`}
                        value={option}
                        key={option}
                        label={option}
                        checked={selectedOptions.includes(option)}
                        disabled={quizDisabled}
                        onChange={handleCheckboxChanged}
                    />
                );
            })}

            <Feedback feedbackCorrect={feedbackCorrect} feedbackWrong={feedbackWrong} />
        </Pane>
    );
};
